import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'wouter';
import { AssetGrid } from '../components/AssetGrid';
import { useQuickLook } from '../lib/use-quick-look';
import { AssetTable } from '../components/AssetTable';
import { useInfiniteAssets } from '../lib/use-infinite-assets';
import { SEARCH_RESULT_LIMIT } from '../../shared/asset-constants';
import { FilterBar, type AssetTypeFilter, type FilterBarProps } from '../components/FilterBar';
import { LibraryFilterChips } from '../components/LibraryFilterChips';
import { LibraryViewTabs } from '../components/LibraryViewTabs';
import { ResultsToolbar } from '../components/ResultsToolbar';
import {
  hasActiveFilters as filtersActive,
  stripFilterParams,
  stripFacetParams,
  stripChipFacet,
  type ChipFacet,
} from '../lib/active-filters';
import { activeLibraryView, applyLibraryView, type LibraryView } from '../lib/library-views';
import { AssetGridSkeleton } from '../components/Skeleton';
import { SelectionProvider } from '../components/SelectionProvider';
import { BulkActionBar } from '../components/BulkActionBar';
import { Modal } from '../components/ui/Modal';
import { I } from '../lib/icons';
import { saveLibraryAssetIds } from '../lib/library-neighbors';
import { resolveSearchBanner } from '../lib/search-banner';
import { useSelection } from '../lib/selection-context';
import { useLayoutUser } from '../lib/layout-context';
import { buildByFilterParams, hasDiscriminatingFilter } from '../lib/asset-filter-params';
import { api } from '../lib/api';
import { parseView, type ViewMode } from '../lib/parse-view';
import { parseGridCols, GRID_COLS_DEFAULT, type GridCols } from '../lib/parse-grid-cols';
import { parseSort, parseDir, defaultDirFor, type SortKey, type SortDir } from '../lib/parse-sort';
import { toggleCsv } from '../lib/toggle-csv';
import { parseStatus } from '../lib/parse-status';
import { formatBytes } from '../lib/format-bytes';
import { formatRelative } from '../lib/format-relative';
import type { AssetStats, StatsState } from '../../shared/asset-stats-types';
import type { AssetStatusFilter } from '../../shared/asset-constants';
import { parseTagFilter } from '../../shared/category-validation';
import { PAGE_H1_CLASS } from '../components/ui/page-heading';
import { useUploadModal } from '../lib/upload-context';
import { lazyWithRetry } from '../lib/lazyWithRetry';
import { useContextualTip } from '../lib/use-contextual-tip';
import { ASSETS_CHANGED, FAVORITES_CHANGED, type FavoritesChangedDetail } from '../lib/events';
import { toggleAssetFavorite } from '../lib/favorite-toggle';
import { useToast } from '../lib/toast-context';

// Overlay/Dialog erst beim Öffnen laden (Entry-Chunk, `bundle:check`).
const QuickLookOverlay = lazyWithRetry(() =>
  import('../components/QuickLookOverlay').then((m) => ({ default: m.QuickLookOverlay })),
);
const BulkTagByFilterModal = lazyWithRetry(() =>
  import('../components/BulkTagByFilterModal').then((m) => ({
    default: m.BulkTagByFilterModal,
  })),
);

const VALID_TYPES: ReadonlySet<AssetTypeFilter> = new Set(['all', 'img', 'video', 'pdf', 'logo']);

function parseType(value: string | null): AssetTypeFilter {
  if (value && VALID_TYPES.has(value as AssetTypeFilter)) return value as AssetTypeFilter;
  return 'all';
}

/**
 * Filter-State lebt in der URL — damit kann ein Klick auf einen Tag/eine Facette
 * im AssetInspector die Library auf diesen Filter scoped öffnen
 * (`/?q=tag` bzw. `/?kategorie=…`). Das Such-Input lebt seit 2026-06-08 in
 * <GlobalSearch> (Topbar, app-weit); diese Seite liest `?q=` nur noch.
 *
 * Multi-Select-Workflow läuft über `<SelectionProvider>` — Children konsumieren
 * via `useSelection()`. Selection cleart automatisch bei Filter-Change
 * (siehe `LibraryPageContent`-Effect) und bei ESC-Press (siehe Provider).
 */
export function LibraryPage() {
  return (
    <SelectionProvider>
      <LibraryPageContent />
    </SelectionProvider>
  );
}

// Empty-State-CTA-Button (geteilt: Filter-Reset / Upload / OR-Fallback / Zur-
// exakten-Suche) — eine Klasse, mehrere Affordanzen.
const EMPTY_STATE_BTN =
  'inline-block px-4 py-2 bg-brand-green-700 text-brand-cream rounded-md text-sm font-semibold hover:bg-brand-yellow hover:text-brand-green-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2';

function LibraryPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = parseType(searchParams.get('type'));
  const sort: SortKey = parseSort(searchParams.get('sort'));
  // Wenn `dir` fehlt oder ungültig: Default-Direction der jeweiligen Spalte
  // (name/type → asc, recent/size → desc).
  const dir: SortDir = parseDir(searchParams.get('dir')) ?? defaultDirFor(sort);
  const view: ViewMode = parseView(searchParams.get('view'));
  const gridCols: GridCols = parseGridCols(searchParams.get('cols'));
  const status: AssetStatusFilter = parseStatus(searchParams.get('status'));
  const urlQuery = searchParams.get('q') ?? '';
  // Basisnamen-Stapel: `?stacks=1` = gruppierter Modus; `?stack=<key>` = Drill-in
  // in einen Stapel (gewinnt über stacks). drillStack ist der lowercase Key.
  const stacks = searchParams.get('stacks') === '1';
  const drillStack = (searchParams.get('stack')?.trim() ?? '') || null;
  // Schnellzugriff-Boolean-Filter (Dashboard-Triage-Karten, 2026-06-15). Backend
  // versteht beide; `mine` löst server-seitig auf den ctx-User auf. In
  // FILTER_PARAM_KEYS eingetragen → „Alle zurücksetzen" cleart sie.
  const untagged = searchParams.get('untagged') === '1';
  const mine = searchParams.get('mine') === '1';
  // Such-Verknüpfungs-Modus (Sprint 5): `?match=or` = Relaxed-OR-Fallback, nur
  // nach 0 AND-Treffern angeboten. KEIN Filter, sondern ein q-gekoppelter
  // Modus-Modifier → bewusst NICHT in FILTER_PARAM_KEYS (clearQuery +
  // stripFilterParams löschen ihn explizit). Default 'and'.
  const matchMode: 'and' | 'or' = searchParams.get('match') === 'or' ? 'or' : 'and';
  const isOrMode = matchMode === 'or';
  // Sprint C6: tag-Filter (Sidebar-Kategorien). `tagKey` ist die EINZIGE
  // kanonische Repräsentation — ein normalisierter, deduplizierter,
  // gecappter String. Effects nutzen `tagKey` als Dep (kein instabiles Array)
  // und bauen den Request-Param direkt daraus.
  const tagKey = parseTagFilter(searchParams.get('tag')).join(',');
  // Tag-Filter als Array fürs Rendering (Mirror produkt/verwendung) — aus dem
  // normalisierten `tagKey` abgeleitet (lowercase/dedup/cap). FilterBar-Combobox
  // + ActiveFilters konsumieren das; `tagKey` bleibt der stabile Effect-Dep.
  const tag = tagKey ? tagKey.split(',') : [];
  // Taxonomie-Facetten-Filter (v2). `kategorie` = Top-Level Single-Select
  // (Sidebar + FilterBar): Roh-UUID oder null. Produkt/Verwendung = Multi-Select:
  // kommaseparierte UUID-Liste. Wir halten den ROH-Param als stabilen String-Key
  // (Mirror `tagKey`) für Effect-Deps + API-Param und leiten das Array nur fürs
  // Rendering ab (Array-Identität wäre instabil → Refetch-Loop).
  const kategorieParam = searchParams.get('kategorie') ?? '';
  const produktParam = searchParams.get('produkt') ?? '';
  const verwendungParam = searchParams.get('verwendung') ?? '';
  // Kategorie ist seit 2026-06-11 Multi-Select (csv, Mirror produkt) — war
  // Single. Cascade-Anker bleibt: gewählte Kategorien → Vereinigung ihrer Produkte.
  const kategorie = kategorieParam ? kategorieParam.split(',') : [];
  const produkt = produktParam ? produktParam.split(',') : [];
  const verwendung = verwendungParam ? verwendungParam.split(',') : [];

  // Suche lebt seit dem Topbar-Umzug in <GlobalSearch> (Layout-Ebene). Hier
  // nur noch LESEN: `urlQuery` (`?q=`) ist die Single-Source-of-Truth, fließt
  // direkt in Fetch + hasActiveFilters. Kein lokaler Such-State mehr.
  const [refetchKey, setRefetchKey] = useState(0);
  // Mobile-Filter-Drawer (<md): das volle Panel als Bottom-Sheet (die Chip-Zeile
  // gibt es auf allen Breiten; der Drawer bleibt als Mobile-Gesamtansicht).
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Stats-State als Tri-State-Union (Codex A1) — analog `LoadState` für
  // assets-fetch. Vermeidet `null`-Überladung (loading vs error vs ok).
  const [stats, setStats] = useState<StatsState>({ status: 'loading' });
  // Sprint 5: OR-Trefferzahl für den Zero-Result-Fallback-Banner. `null` =
  // unbekannt/loading/error/nicht-anwendbar → kein Banner. Nur befüllt, wenn die
  // AND-Suche 0 Treffer hat (konditionaler Fetch unten).
  const [orFallbackCount, setOrFallbackCount] = useState<number | null>(null);
  const selection = useSelection();
  const { open: openUpload } = useUploadModal();
  // „Alle N Treffer taggen" (Roadmap 1b) ist Admin-only (Backend = assertAdmin).
  const isAdmin = useLayoutUser()?.role === 'Admin';
  // Offen-State trägt das beim Klick eingefrorene Filter-Set (stabile Identität
  // über die Modal-Lebensdauer → dryRun-Effect läuft genau einmal).
  const [bulkTagByFilter, setBulkTagByFilter] = useState<Record<string, string> | null>(null);

  // Nach erfolgreichem Upload (globales Modal dispatcht ASSETS_CHANGED) die
  // Asset-Liste neu laden, damit neue Uploads sofort erscheinen.
  useEffect(() => {
    const onAssetsChanged = () => setRefetchKey((k) => k + 1);
    window.addEventListener(ASSETS_CHANGED, onAssetsChanged);
    return () => window.removeEventListener(ASSETS_CHANGED, onAssetsChanged);
  }, []);

  // Filter-/Sort-Query OHNE `cursor` — der STABILE String-Dep des Infinite-
  // Hooks. Single-Source für Library- (Pagination) + nichts sonst; der Stats-
  // Fetch baut seine eigene (gewollt abweichende) Param-Liste. Jeder Wechsel
  // hier → Hook resettet Accumulator+Cursor+Scroll und lädt Seite 1 (B4).
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (type !== 'all') params.set('type', type);
    if (urlQuery) params.set('q', urlQuery);
    // Sort + dir nur senden wenn non-default (default = recent + desc); beide
    // zusammen, damit das Backend nicht raten muss.
    if (sort !== 'recent' || dir !== 'desc') {
      params.set('sort', sort);
      params.set('dir', dir);
    }
    if (status !== 'approved') params.set('status', status);
    if (tagKey) params.set('tag', tagKey);
    if (kategorieParam) params.set('kategorie', kategorieParam);
    if (produktParam) params.set('produkt', produktParam);
    if (verwendungParam) params.set('verwendung', verwendungParam);
    // Stapel: Drill-in (`?stack=`) gewinnt über den gruppierten Modus (`?stacks=1`).
    if (drillStack) params.set('stack', drillStack);
    else if (stacks) params.set('stacks', '1');
    if (untagged) params.set('untagged', '1');
    if (mine) params.set('mine', '1');
    // Sprint 5: OR-Modus an die Liste durchreichen (relaxt die Token-Verknüpfung).
    if (isOrMode) params.set('match', 'or');
    const s = params.toString();
    return s ? `?${s}` : '';
  }, [type, sort, dir, status, urlQuery, tagKey, kategorieParam, produktParam, verwendungParam, stacks, drillStack, untagged, mine, isOrMode]);

  // Infinite-Scroll-Fetch (B3: Hook lebt hier, NICHT in Grid/Table). Reset bei
  // queryString-Wechsel ODER refetchKey-Bump (Upload/Bulk via ASSETS_CHANGED).
  const lib = useInfiniteAssets(queryString, refetchKey);

  // J/K-Bridge (B5): nach JEDEM Accumulator-Push die geladene, geordnete
  // ID-Liste persistieren → AssetDetailPage + Quick-Look blättern über die Range.
  useEffect(() => {
    saveLibraryAssetIds(
      lib.assets.map((a) => a.id),
      !lib.hasMore,
    );
  }, [lib.assets, lib.hasMore]);

  // ── Triage-Quick-Look (Session 30) ──────────────────────────────────────
  // Overlay-State + Neighbor-Nav. Die gehoverte Karten-ID liegt in einer Ref
  // (KEIN State → kein Re-Render beim Hovern; Session-23-Perf-Leitplanke).
  // onCardHover/onGridLeave/quickLook.open sind stabil → kein memo(AssetCard)-Break.
  const quickLook = useQuickLook();
  // Onboarding: einmaliger Tipp beim ersten Öffnen der Schnellansicht.
  useContextualTip('quick-look', quickLook.openId != null, {
    anchor: 'quick-look',
    title: 'Schnellansicht',
    description:
      'Leertaste (oder das Auge) öffnet die Vorschau mit allen Details — ohne die Seite zu verlassen. J/K springt zum nächsten Asset.',
    side: 'left',
  });
  const hoveredIdRef = useRef<string | null>(null);
  const onCardHover = useCallback((id: string) => {
    hoveredIdRef.current = id;
  }, []);
  const onGridLeave = useCallback(() => {
    hoveredIdRef.current = null;
  }, []);
  // Leertaste auf der gehoverten Karte öffnet Quick-Look. Nur wenn NICHTS
  // fokussiert ist (e.target === body) — sonst gewinnt das fokussierte Element
  // sein Space-Verhalten (Button-Aktivierung / Scroll). Kein Quick-Look über
  // einem offenen Dialog.
  const openQuickLook = quickLook.open;
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key !== ' ' && e.code !== 'Space') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.target !== document.body) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      // Space gehört auf der Library der Quick-Look-Affordanz → den Browser-
      // Default (Seite runterscrollen) IMMER unterdrücken, sobald wir auf body
      // sind + kein Dialog offen. Sonst sprang Space ohne gehoverte Karte ins
      // Leere (weißer Scroll-Sprung). Ohne gehoverte Karte → No-op statt Scroll.
      e.preventDefault();
      const id = hoveredIdRef.current;
      if (!id) return;
      openQuickLook(id);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openQuickLook]);

  // ── Favoriten-Tracker + „F"-Shortcut (Roadmap 3a Phase 3) ───────────────
  // `favoritedRef` = aktueller Favoriten-Stand pro id: seeded aus der Liste
  // (Server-Wahrheit, re-seed bei Refetch), aktuell gehalten via FAVORITES_CHANGED
  // (lokale Toggles von überall). Quelle der Wahrheit für „F", da die Card ihren
  // Stern intern toggelt und `lib.assets[].favorited` dann stale wäre.
  const favToast = useToast();
  const favoritedRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => {
    // Nur NEUE ids seeden — ein bereits getrackter Stand (initial ODER via
    // FAVORITES_CHANGED lokal getoggelt) bleibt autoritativ und wird NICHT vom
    // stale Listenwert beim Infinite-Scroll-Append überschrieben.
    for (const a of lib.assets) {
      if (!favoritedRef.current.has(a.id)) favoritedRef.current.set(a.id, !!a.favorited);
    }
  }, [lib.assets]);
  useEffect(() => {
    function onChanged(e: Event): void {
      const detail = (e as CustomEvent<FavoritesChangedDetail>).detail;
      if (!detail) return;
      favoritedRef.current.set(detail.assetId, detail.favorited);
    }
    window.addEventListener(FAVORITES_CHANGED, onChanged);
    return () => window.removeEventListener(FAVORITES_CHANGED, onChanged);
  }, []);

  // „F" auf der gehoverten Karte togglet den Favoriten. Guards wie der Space-
  // Handler: nichts fokussiert (`e.target===body`) + KEIN Dialog offen (→ feuert
  // NICHT wenn die Quick-Look-Overlay offen ist, deren eigener F-Handler greift →
  // kein Doppel-Toggle). Die gehoverte Karte synct ihren Stern via FAVORITES_CHANGED.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target !== document.body) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      const id = hoveredIdRef.current;
      if (!id) return;
      e.preventDefault();
      const current = favoritedRef.current.get(id) ?? false;
      void toggleAssetFavorite(id, current).catch(() => {
        favToast.error('Favorisieren fehlgeschlagen.');
      });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [favToast]);

  // Stats-Fetch parallel zum Asset-Fetch — eigener Effect, gleiche deps
  // MINUS sort/dir (Aggregation ist sort-invariant). Tri-State-Union
  // (Codex A1): {loading} | {error} | {ok, stats}. Beide Fetches sind
  // unabhängig, Stats-Failure killt Library-Liste nicht und vice versa.
  useEffect(() => {
    let cancelled = false;
    setStats({ status: 'loading' });

    const params = new URLSearchParams();
    if (type !== 'all') params.set('type', type);
    if (urlQuery) params.set('q', urlQuery);
    if (status !== 'approved') params.set('status', status);
    if (tagKey) params.set('tag', tagKey);
    if (kategorieParam) params.set('kategorie', kategorieParam);
    if (produktParam) params.set('produkt', produktParam);
    if (verwendungParam) params.set('verwendung', verwendungParam);
    // Sprint 5: mine/untagged jetzt auch in /stats (Backend versteht sie) → der
    // Header-Count respektiert die Schnellzugriff-Filter (zuvor latente Divergenz).
    if (untagged) params.set('untagged', '1');
    if (mine) params.set('mine', '1');
    // OR-Modus muss in die Stats-Count, sonst stale Trefferzahl beim AND↔OR-Toggle.
    if (isOrMode) params.set('match', 'or');

    (async () => {
      try {
        const data = await api<AssetStats>(
          `/api/assets/stats${params.toString() ? `?${params.toString()}` : ''}`,
        );
        if (cancelled) return;
        setStats({ status: 'ok', stats: data });
      } catch {
        if (cancelled) return;
        setStats({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, status, urlQuery, tagKey, kategorieParam, produktParam, verwendungParam, untagged, mine, isOrMode, refetchKey]);

  // Sprint 5 — Zero-Result-OR-Fallback: liefert die AND-Suche 0 Treffer, holen
  // wir die OR-Trefferzahl (≥1 Token) aus /stats?match=or, um den Fallback-Banner
  // anzubieten. Konditional (nur 0-AND-Fall), NICHT im Stapel-/Drill-in-Modus
  // (/stats kennt stacks/stack nicht → N würde lügen, Codex Contract-1). Keine
  // <2-Token-Sparmaßnahme (Codex C2: foo-bar wäre client 1, server 2 Tokens) —
  // das N>0-Gate unterdrückt den Banner im echten 1-Token-Fall ohnehin. Abort/
  // Reset-Pattern wie der Stats-Effect. Fehler/null → kein Banner.
  useEffect(() => {
    if (isOrMode || urlQuery === '' || stacks || drillStack || lib.status !== 'ok' || lib.assets.length !== 0) {
      setOrFallbackCount(null);
      return;
    }
    let cancelled = false;
    setOrFallbackCount(null);
    const params = new URLSearchParams();
    if (type !== 'all') params.set('type', type);
    params.set('q', urlQuery);
    if (status !== 'approved') params.set('status', status);
    if (tagKey) params.set('tag', tagKey);
    if (kategorieParam) params.set('kategorie', kategorieParam);
    if (produktParam) params.set('produkt', produktParam);
    if (verwendungParam) params.set('verwendung', verwendungParam);
    if (untagged) params.set('untagged', '1');
    if (mine) params.set('mine', '1');
    params.set('match', 'or');
    (async () => {
      try {
        const data = await api<AssetStats>(`/api/assets/stats?${params.toString()}`);
        if (cancelled) return;
        setOrFallbackCount(data.total);
      } catch {
        if (cancelled) return;
        setOrFallbackCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [type, status, urlQuery, tagKey, kategorieParam, produktParam, verwendungParam, untagged, mine, stacks, drillStack, isOrMode, lib.status, lib.assets.length, refetchKey]);

  // Selection cleart bei Filter-Change. URL ist Source of Truth, also
  // reagieren wir auf die berechneten Filter-Werte. Bei Filter-Change ist die
  // Library-Liste komplett anders — Selection-Carry-Over wäre verwirrend
  // (anchor-IDs könnten nicht mehr in der gefilterten Liste sein).
  // Sort/dir-Change cleart auch — Order-Wechsel macht anchor-relative
  // shift-range nicht mehr sinnvoll. Density-Change cleart NICHT (rein
  // visual, kein Daten-Layout-Wechsel).
  // isOrMode-Wechsel (AND↔OR) cleart auch: das Ergebnis-Set ist ein anderes →
  // Selection-Carry-Over (anchor-IDs evtl. nicht mehr drin) wäre verwirrend.
  useEffect(() => {
    selection.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, sort, dir, status, urlQuery, tagKey, kategorieParam, produktParam, verwendungParam, stacks, drillStack, untagged, mine, isOrMode]);

  function setType(next: AssetTypeFilter): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'all') params.delete('type');
      else params.set('type', next);
      return params;
    });
  }

  // Redesign S2 Phase 2a: × an einem Facetten-Chip entfernt NUR diese Facette
  // (Produkt & Kategorie = beide Params) — alle anderen Params bleiben.
  function clearChipFacet(facet: ChipFacet): void {
    setSearchParams((prev) => stripChipFacet(prev, facet));
  }

  // Ansichten-Tabs: URL-Preset anwenden (mine/status), übrige Filter bleiben.
  function selectView(view: LibraryView): void {
    setSearchParams((prev) => applyLibraryView(prev, view));
  }

  // Schnellzugriff-Filter einzeln entfernen (Chip-×).
  function clearUntagged(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('untagged');
      return params;
    });
  }
  function clearMine(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('mine');
      return params;
    });
  }

  // Multi-Select-Toggle für Kategorie/Produkt/Verwendung: aktuelle Liste aus der
  // URL lesen, `id` ein-/ausschalten, leere Liste → Param löschen (hält die URL
  // clean), sonst kommasepariert setzen. Server parst via parseUuidFilter
  // (OR-within / AND-across). Kategorie ist seit 2026-06-11 ebenfalls
  // Multi-Select (war Single-Select-Setter).
  function toggleFacet(key: 'kategorie' | 'produkt' | 'verwendung', id: string): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const next = toggleCsv(params.get(key) ?? '', id);
      if (next) params.set(key, next);
      else params.delete(key);
      return params;
    });
  }

  // Tag-Filter-Toggle (freie Tags) — analog toggleFacet, eigener `?tag=`-Param.
  // `name` ist der lowercase Tag-Name aus der Combobox; parseTagFilter beim Lesen
  // normalisiert/dedupt/cappt ohnehin. Leere Liste → Param raus (URL clean).
  function toggleTag(name: string): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const next = toggleCsv(params.get('tag') ?? '', name);
      if (next) params.set('tag', next);
      else params.delete('tag');
      return params;
    });
  }

  // Entfernt NUR den Freitext `?q=` — analog clearTag. GlobalSearch (Topbar)
  // synct sein Input aus der URL → das Suchfeld leert sich mit. Sprint 5: löscht
  // auch `match` (der OR-Modus ist q-gekoppelt — ohne Query kein verwaister Modus;
  // deckt Suchfeld-Leeren UND Query-Chip-✕ ab, beide rufen clearQuery).
  function clearQuery(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('q');
      params.delete('match');
      return params;
    });
  }

  // Sprint 5: OR-Fallback aktivieren (Banner-Klick) — setzt `?match=or` zusätzlich
  // zum bestehenden `?q=`. disableOrMode = „Zur exakten Suche" (zurück zu AND).
  function enableOrMode(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('match', 'or');
      return params;
    });
  }
  function disableOrMode(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('match');
      return params;
    });
  }

  function setSort(key: SortKey, newDir: SortDir): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (key === 'recent' && newDir === 'desc') {
        // Default-State → URL clean halten
        params.delete('sort');
        params.delete('dir');
      } else {
        params.set('sort', key);
        params.set('dir', newDir);
      }
      return params;
    });
  }

  // Sprint Quick-Wins Item 2: status-Filter URL-State (preserves all other params).
  function setStatus(next: AssetStatusFilter): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'approved') params.delete('status');
      else params.set('status', next);
      return params;
    });
  }

  // Sprint Quick-Wins Item 1: Suggestion-Commit (Click oder Enter).
  // MUSS pending Library-debounce clearen (Codex K2) damit URL sofort
  // updated wird, nicht erst nach 300ms.
  function setView(next: ViewMode): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'grid') params.delete('view');
      else params.set('view', next);
      return params;
    });
  }

  // Spalten-Regler (Grid-Ansicht). Default (5) → Param raus, sonst gesetzt.
  function setGridCols(next: GridCols): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === GRID_COLS_DEFAULT) params.delete('cols');
      else params.set('cols', String(next));
      return params;
    });
  }

  // Stapel-Toggle: `?stacks=1` an/aus. Modus-Wechsel verlässt immer ein offenes
  // Drill-in (`?stack` raus).
  function setStacks(next: boolean): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('stacks', '1');
      else params.delete('stacks');
      params.delete('stack');
      return params;
    });
  }

  // Drill-in öffnen (Klick auf Stapel-Tile): `?stack=<key>` setzen, aktive Filter
  // + `?stacks` bleiben erhalten (für den kontextabhängigen Zurück-Link).
  // useCallback: stabile Identität → die memoized AssetCard re-rendert beim
  // Scrollen NICHT (sonst bräche memo bei jedem LibraryPage-Re-Render, z.B.
  // Page-Load via setAssets).
  const openStack = useCallback(
    (stackKey: string): void => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('stack', stackKey);
        return params;
      });
    },
    [setSearchParams],
  );

  // Drill-in verlassen (Zurück): nur `?stack` raus, `?stacks` bleibt.
  function closeStack(): void {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('stack');
      return params;
    });
  }

  // Löscht alle Filter-Params (inkl. `q`), behält Präsentation: view/sort/dir
  // (IA Befund 2b — Sort ist kein Filter, soll beim Filter-Reset NICHT
  // verloren gehen). GlobalSearch synct sein Input aus der URL → das
  // Topbar-Suchfeld leert sich mit. Transform: stripFilterParams (pure).
  function clearAllFilters(): void {
    setSearchParams((prev) => stripFilterParams(prev), { replace: true });
  }

  // Ein-Klick „Taxonomie zurücksetzen" (IA-Follow-up): clears nur den Browse-
  // Facetten-Pfad (Kategorie/Produkt/Verwendung), behält Typ/Status/Suche/Tag.
  // Abgegrenzt von clearAllFilters (Chip-Leiste).
  function clearFacets(): void {
    setSearchParams((prev) => stripFacetParams(prev), { replace: true });
  }

  function refetchLibrary(): void {
    setRefetchKey((k) => k + 1);
  }

  // IA Befund 2a: Sort/dir sind Präsentation, kein Filter → NICHT in
  // hasActiveFilters (ein reiner Sort-Wechsel triggert keine Reset-Affordanz /
  // keinen „keine Treffer"-Empty-State). Pure Helper liest die Filter-Params
  // direkt (alle Setter löschen ihren Param beim Default → Presence = aktiv).
  const hasActiveFilters = filtersActive(searchParams);

  // Roadmap 1b: „Alle <N> Treffer taggen"-Affordanz — Admin-only, sichtbar nur
  // bei ≥1 DISKRIMINIERENDEM Filter (status/match allein = Default-Ansicht,
  // zählen NICHT) UND total>0. stats.total ist nur der Banner-Hint; der echte,
  // autoritative Count kommt aus dem Modal-dryRun (buildAssetWhere, kein Drift).
  const statsTotal = stats.status === 'ok' ? stats.stats.total : 0;
  const canBulkTagByFilter =
    isAdmin && statsTotal > 0 && hasDiscriminatingFilter(searchParams);

  // Sprint 5: Banner-/Empty-State-Entscheidung via pure Helper (search-banner.ts,
  // unit-getestet) — OR-aktiv-Banner / OR-empty / OR-Fallback-Angebot / normal.
  const searchBanner = resolveSearchBanner({
    isOrMode,
    listStatus: lib.status,
    listLength: lib.status === 'ok' ? lib.assets.length : 0,
    orFallbackCount,
  });

  // Echte Gesamtzahl der gefilterten Treffer (Header-Stats) — für „X von M
  // geladen" + Such-Cap-Hinweis. `null` solange Stats laden/fehlen.
  const totalLabel =
    stats.status === 'ok' ? stats.stats.total.toLocaleString('de-DE') : null;
  // Such-Cap (C5): bei aktivem `q` ist die Liste auf SEARCH_RESULT_LIMIT
  // gedeckelt (kein Cursor) — wenn es mehr Treffer gibt, ehrlich kommunizieren.
  const searchCapped =
    urlQuery !== '' && stats.status === 'ok' && stats.stats.total > SEARCH_RESULT_LIMIT;

  // Header-Stats-Render: count immer visible, bytes + lastUpload nur ≥md
  // (Mobile-Header bleibt schmal). Tausenderpunkte via toLocaleString.
  // Edge-Case: lastUpload === null mit total > 0 (theoretisch) → kein
  // Datum-Segment append'en (strict null-check vermeidet formatRelative-call
  // mit null).
  function renderHeaderStats() {
    if (stats.status === 'loading') {
      return (
        <span
          className="inline-block w-48 h-4 bg-ink-200 rounded animate-pulse"
          aria-hidden="true"
        />
      );
    }
    if (stats.status === 'error') {
      // Silent skip — Stats-Failure ist nicht user-actionable, Library-Liste
      // lädt unabhängig. Header-Layout bleibt durch leeren span stabil.
      return <span aria-hidden="true" />;
    }
    const { total, totalBytes, lastUpload } = stats.stats;
    const countStr = total.toLocaleString('de-DE');
    const sizeStr = formatBytes(totalBytes);
    return (
      <span className="text-xs font-mono text-brand-green-600 tracking-wide">
        {countStr} Assets
        <span className="hidden md:inline">
          {' · '}
          {sizeStr}
          {lastUpload && (
            <>
              {' · '}
              zuletzt {formatRelative(lastUpload)}
            </>
          )}
        </span>
      </span>
    );
  }

  // Filter-Props einmal definiert → genutzt von den Chip-Popovers (je eine
  // Sektion) UND vom vollen Panel im Mobile-Drawer.
  const filterProps: FilterBarProps = {
    type,
    onTypeChange: setType,
    status,
    onStatusChange: setStatus,
    kategorie,
    onKategorieToggle: (id) => toggleFacet('kategorie', id),
    produkt,
    onProduktToggle: (id) => toggleFacet('produkt', id),
    verwendung,
    onVerwendungToggle: (id) => toggleFacet('verwendung', id),
    tag,
    onTagToggle: toggleTag,
    onResetFacets: clearFacets,
    stats,
  };

  // Aktiver Ansichten-Tab (null = Preset-Params passen zu keinem Tab).
  const activeView = activeLibraryView(searchParams);

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <div className="mb-6 flex items-baseline justify-between gap-4 flex-wrap">
        <h1 className={PAGE_H1_CLASS}>Alle Assets</h1>
        <div className="flex items-baseline gap-4 flex-shrink-0">
          {renderHeaderStats()}
          {/* Header-„FILTER ZURÜCKSETZEN" 2026-06-10 entfernt: „Alle zurücksetzen"
              lebt jetzt als „Alle Filter zurücksetzen" in der Chip-Zeile über den
              Ergebnissen. */}
        </div>
      </div>

      {/* Redesign S2 Phase 2a (2026-10-10): kein rechtes Filter-Panel mehr.
          Über dem Raster: Ansichten-Tabs, darunter die Chip-Zeile (Facetten-Chips
          mit Popover + aktive Such-/Schnellzugriff-Chips + Reset) und rechts
          Sort/Ansicht. Mobile: Chips scrollen horizontal, Toolbar darunter. */}
      <div className="min-w-0">
      <LibraryViewTabs active={activeView} onSelect={selectView} />
      <div className="mb-4 flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
        <LibraryFilterChips
          filters={filterProps}
          onClearFacet={clearChipFacet}
          q={urlQuery}
          untagged={untagged}
          // Der Tab „Meine Uploads" zeigt mine schon — Chip nur, wenn kein Tab es tut.
          mine={mine && activeView !== 'mine'}
          anyActive={hasActiveFilters}
          onClearQuery={clearQuery}
          onClearUntagged={clearUntagged}
          onClearMine={clearMine}
          onClearAll={clearAllFilters}
          leading={
            // Mobile (<md): das volle Panel als Bottom-Sheet-Drawer (bleibt).
            <button
              type="button"
              onClick={() => setFilterDrawerOpen(true)}
              className="md:hidden shrink-0 inline-flex items-center gap-2 h-11 px-3.5 rounded-control text-xs font-semibold bg-white border border-ink-200 text-brand-green-800 hover:border-brand-green-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
            >
              <I.filter size={15} />
              Filter
              {hasActiveFilters && (
                <span className="w-1.5 h-1.5 rounded-full bg-brand-green-700" aria-hidden="true" />
              )}
            </button>
          }
        />
        <ResultsToolbar
          sort={sort}
          dir={dir}
          onSortChange={setSort}
          view={view}
          onViewChange={setView}
          gridCols={gridCols}
          onGridColsChange={setGridCols}
          stacks={stacks || drillStack !== null}
          onStacksChange={setStacks}
        />
      </div>

      {/* Roadmap 1b: „Alle <N> Treffer taggen" — Admin-only, nur bei
          diskriminierendem Filter + Treffern. Friert beim Klick das Filter-Set
          ein (stabile Modal-Props) → Modal holt den autoritativen dryRun-Count. */}
      {canBulkTagByFilter && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setBulkTagByFilter(buildByFilterParams(searchParams))}
            className="inline-flex items-center gap-2 rounded-md border border-brand-green-700/30 bg-white px-3.5 py-2 text-xs font-semibold text-brand-green-800 hover:border-brand-green-700/60 hover:bg-brand-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2"
          >
            <I.tag size={14} />
            Alle {statsTotal.toLocaleString('de-DE')} Treffer taggen
          </button>
        </div>
      )}

      {/* Drill-in-Header: Zurück-Link (kontextabhängig) + Stapel-Titel + Anzahl. */}
      {drillStack && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            type="button"
            onClick={closeStack}
            className="inline-flex items-center gap-1 text-xs2 font-semibold text-brand-green-600 hover:text-brand-green-800 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-1"
          >
            ← {stacks ? 'Zurück zu den Stapeln' : 'Alle Assets'}
          </button>
          <span className="text-sm text-brand-green-700">
            Stapel: <strong className="font-semibold">{drillStack}</strong>
            {lib.status === 'ok' && (
              <span className="text-brand-green-600">
                {' · '}
                {lib.assets.length}
                {lib.hasMore ? '+' : ''} {lib.assets.length === 1 ? 'Datei' : 'Dateien'}
              </span>
            )}
          </span>
        </div>
      )}

      {lib.status === 'loading' && <AssetGridSkeleton />}

      {lib.status === 'error' && (
        <div className="p-4 bg-red-50 rounded-md text-sm text-red-600" role="alert">
          {lib.errorMessage}
        </div>
      )}

      {lib.status === 'ok' && lib.assets.length === 0 && (
        <div className="text-center py-16 bg-brand-cream rounded-xl border border-ink-200">
          {searchBanner === 'or-empty' ? (
            // Sprint 5: OR-Modus aktiv, aber AUCH ≥1-Token fand nichts (z.B.
            // Direct-Link ?q=…&match=or). KEIN OR-Banner über leerem Grid (Codex
            // R1-F4) — stattdessen diese Empty-Variante mit „Zur exakten Suche".
            <>
              <p className="text-lg text-brand-green-700 font-semibold mb-2">Keine Assets gefunden</p>
              <p className="text-sm text-brand-green-700 mb-5">
                Auch keine Treffer für mindestens einen Begriff. Andere Suche oder Filter versuchen.
              </p>
              <button type="button" onClick={disableOrMode} className={EMPTY_STATE_BTN}>
                Zur exakten Suche
              </button>
            </>
          ) : searchBanner === 'or-fallback' ? (
            // Sprint 5 Zero-Result-OR-Fallback: 0 Treffer für ALLE Begriffe, aber
            // N für mindestens einen → Fallback anbieten (N = ungekappter OR-Total).
            <>
              <p className="text-lg text-brand-green-700 font-semibold mb-2">Keine Treffer für alle Begriffe</p>
              <p className="text-sm text-brand-green-700 mb-5">
                Für mindestens einen Begriff{' '}
                {mine || untagged || type !== 'all' || tagKey || kategorieParam || produktParam || verwendungParam
                  ? 'in dieser Auswahl '
                  : ''}
                gibt es {(orFallbackCount ?? 0).toLocaleString('de-DE')} Treffer.
              </p>
              <button type="button" onClick={enableOrMode} className={EMPTY_STATE_BTN}>
                {(orFallbackCount ?? 0).toLocaleString('de-DE')} Treffer anzeigen
              </button>
            </>
          ) : (
            <>
              <p className="text-lg text-brand-green-700 font-semibold mb-2">Keine Assets gefunden</p>
              <p className="text-sm text-brand-green-700 mb-5">
                {hasActiveFilters
                  ? 'Keine Treffer für diese Filter. Filter zurücksetzen oder andere Suche versuchen.'
                  : 'Noch keine Assets hochgeladen.'}
              </p>
              {hasActiveFilters ? (
                <button type="button" onClick={clearAllFilters} className={EMPTY_STATE_BTN}>
                  Filter zurücksetzen
                </button>
              ) : (
                <button type="button" onClick={() => openUpload()} className={EMPTY_STATE_BTN}>
                  ↑ Erstes Asset hochladen
                </button>
              )}
            </>
          )}
        </div>
      )}

      {lib.status === 'ok' && lib.assets.length > 0 && (
        <>
          {/* Sprint 5: aktiver OR-Modus-Hinweis — NUR bei Treffern (length>0,
              Codex R1-F4: sonst rendert er über dem Empty-State). „Zur exakten
              Suche" schaltet zurück auf AND. (searchBanner==='or-active' ⟺
              isOrMode && length>0, via getestetem Helper.) */}
          {searchBanner === 'or-active' && (
            <div
              className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-lg border border-brand-yellow/60 bg-brand-yellow/15 px-4 py-2.5 text-sm text-brand-green-800"
              role="status"
            >
              <span>
                Keine exakten Treffer für alle Begriffe — zeige Treffer für{' '}
                <strong>mindestens einen</strong> Begriff.
              </span>
              <button
                type="button"
                onClick={disableOrMode}
                className="shrink-0 font-semibold text-brand-green-700 underline underline-offset-2 hover:text-brand-green-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2 rounded"
              >
                Zur exakten Suche
              </button>
            </div>
          )}
          {/* CSS-Swap statt Mount-Switching: beide Views sind permanent
              gemountet, Tailwind-Classes schalten Sichtbarkeit. Auf <md
              wird IMMER Grid gerendert (List-View hidden), unabhängig vom
              view-URL-Param. Vorteil: kein matchMedia-Race, Selection-
              Provider stabil über Resize. Siehe spec-listenansicht.md
              Must-Have-#5. */}
          <div className={view === 'list' ? 'block md:hidden' : 'block'}>
            <AssetGrid
              assets={lib.assets}
              cols={gridCols}
              onStackOpen={openStack}
              hasMore={lib.hasMore}
              loadingMore={lib.loadingMore}
              onLoadMore={lib.loadMore}
              // String-Primitive (`?q=`) → kein memo(AssetCard)-Break (Session 23).
              highlightQuery={urlQuery}
              onHover={onCardHover}
              onLeave={onGridLeave}
              onQuickLook={openQuickLook}
              enableFavorite
            />
          </div>
          <div className={view === 'list' ? 'hidden md:block' : 'hidden'}>
            <AssetTable
              assets={lib.assets}
              sort={sort}
              dir={dir}
              onSortChange={setSort}
              density="regular"
              onStackOpen={openStack}
              hasMore={lib.hasMore}
              loadingMore={lib.loadingMore}
              onLoadMore={lib.loadMore}
              highlightQuery={urlQuery}
              enableFavorite
            />
          </div>

          {/* Nachlade-Footer: Status der geladenen Range — ehrliches „X von M
              geladen" (Must-Have #4), Nachlade-Spinner, inline-Retry bei Fehler
              (bestehende Liste bleibt), Such-Cap-Hinweis (C5). */}
          <div className="mt-5 mb-2 text-center text-xs font-mono text-brand-green-600" role="status" aria-live="polite">
            {lib.loadMoreError ? (
              <button
                type="button"
                onClick={lib.loadMore}
                className="text-red-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded px-2 py-1"
              >
                Nachladen fehlgeschlagen: {lib.loadMoreError} — erneut versuchen
              </button>
            ) : searchCapped ? (
              <span className="text-brand-green-700 font-semibold">
                Top {SEARCH_RESULT_LIMIT.toLocaleString('de-DE')} Treffer{totalLabel ? ` von ${totalLabel}` : ''} — Suche verfeinern
              </span>
            ) : lib.loadingMore ? (
              <span>Lädt weitere …</span>
            ) : lib.hasMore ? (
              <span>
                {lib.assets.length.toLocaleString('de-DE')}
                {totalLabel ? ` von ${totalLabel}` : ''} geladen — weiter scrollen
              </span>
            ) : (
              <span>Alle {lib.assets.length.toLocaleString('de-DE')} geladen</span>
            )}
          </div>
        </>
      )}
      </div>

      {/* Mobile-Filter-Drawer (<md): das volle Panel als Bottom-Sheet. */}
      <Modal
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter"
        placement="bottom-sheet"
      >
        <FilterBar {...filterProps} />
      </Modal>

      <BulkActionBar
        onAfterAction={refetchLibrary}
        assets={lib.status === 'ok' ? lib.assets : []}
      />

      {/* Roadmap 1b: by-filter-Tag-Dialog. `bulkTagByFilter` trägt das beim Klick
          eingefrorene Filter-Set. Erfolg → Library + Stats refetchen (refetchKey
          ist Dep beider Fetches) → neue Tags/Counts sichtbar. */}
      <Suspense fallback={null}>
      {bulkTagByFilter && (
        <BulkTagByFilterModal
          filters={bulkTagByFilter}
          totalHint={statsTotal}
          onClose={() => setBulkTagByFilter(null)}
          onSuccess={() => {
            setBulkTagByFilter(null);
            refetchLibrary();
          }}
        />
      )}

      {/* Triage-Quick-Look (Session 30): nur gemountet wenn offen. J/K wechselt
          die id → useAssetDetail refetcht. */}
      {quickLook.openId && (
        <QuickLookOverlay
          id={quickLook.openId}
          onClose={quickLook.close}
          onNext={quickLook.next}
          onPrev={quickLook.prev}
        />
      )}
      </Suspense>
    </div>
  );
}
